/**
 * 单据类型工具类
 * 统一使用单据编号前缀作为 billType
 */

/**
 * 单据类型前缀列表（按长度降序排列，确保长前缀优先匹配）
 */
const BILL_TYPE_PREFIXES = [
  'QTRK',  // 4字符
  'QTCK',  // 4字符
  'LSTH',  // 4字符
  'LSCK',  // 4字符
  'XSTH',  // 4字符
  'XSCK',  // 4字符
  'XSDD',  // 4字符
  'CGRK',  // 4字符
  'CGTH',  // 4字符
  'CGDD',  // 4字符
  'DBCK',  // 4字符
  'NSLD',  // 4字符
  'NCKD',  // 4字符
  'PDFP',  // 4字符
  'PDLR',  // 4字符
  'ZZD',   // 3字符
  'CXD',   // 3字符
  'QGD',   // 3字符
]

/**
 * 有效的单据类型列表
 */
const VALID_BILL_TYPES = [
  'QGD', 'CGDD', 'CGRK', 'CGTH',
  'LSCK', 'LSTH',
  'XSDD', 'XSCK', 'XSTH',
  'QTRK', 'QTCK',
  'DBCK', 'ZZD', 'CXD'
]

/**
 * 单据类型前缀 -> 中文名称的映射
 */
const BILL_TYPE_NAME_MAP = {
  'QGD': '请购单',
  'CGDD': '采购订单',
  'CGRK': '采购入库',
  'CGTH': '采购退货出库',
  'LSCK': '零售出库',
  'LSTH': '零售退货入库',
  'XSDD': '销售订单',
  'XSCK': '销售出库',
  'XSTH': '销售退货入库',
  'QTRK': '其它入库',
  'QTCK': '其它出库',
  'DBCK': '调拨出库',
  'ZZD': '组装单',
  'CXD': '拆卸单',
  'NSLD': '资产申领',
  'NCKD': '资产出库',
  'PDFP': '盘点复盘',
  'PDLR': '盘点录入',
}

/**
 * 中文名称 -> 单据类型前缀的映射（用于从名称反查）
 */
const NAME_TO_BILL_TYPE_MAP = {}
for (const [code, name] of Object.entries(BILL_TYPE_NAME_MAP)) {
  NAME_TO_BILL_TYPE_MAP[name] = code
}

/**
 * 从单据编号提取类型前缀
 * @param {string} billNo 单据编号，如 "CGDD00001802221"
 * @returns {string} 类型前缀，如 "CGDD"，如果无法识别返回空字符串
 */
export function getBillTypeFromNo(billNo) {
  if (!billNo || typeof billNo !== 'string') {
    return ''
  }
  
  const trimmedNo = billNo.trim().toUpperCase()
  
  for (const prefix of BILL_TYPE_PREFIXES) {
    if (trimmedNo.startsWith(prefix)) {
      return prefix
    }
  }
  
  return ''
}

/**
 * 获取单据类型的中文名称
 * @param {string} billType 单据类型前缀，如 "CGDD"
 * @returns {string} 中文名称，如 "采购订单"
 */
export function getBillTypeName(billType) {
  if (!billType) return ''
  return BILL_TYPE_NAME_MAP[billType] || billType
}

/**
 * 根据中文名称获取单据类型前缀
 * @param {string} billTypeName 中文名称，如 "采购订单"
 * @returns {string} 单据类型前缀，如 "CGDD"
 */
export function getBillTypeByName(billTypeName) {
  if (!billTypeName) return ''
  return NAME_TO_BILL_TYPE_MAP[billTypeName] || billTypeName
}

/**
 * 验证是否为有效的单据类型
 * @param {string} billType 单据类型前缀
 * @returns {boolean} 是否有效
 */
export function isValidBillType(billType) {
  if (!billType) return false
  return VALID_BILL_TYPES.includes(billType.toUpperCase())
}

/**
 * 获取所有支持的单据类型列表
 * @returns {Array<{code: string, name: string}>}
 */
export function getAllBillTypes() {
  return VALID_BILL_TYPES.map(code => ({
    code,
    name: BILL_TYPE_NAME_MAP[code]
  }))
}

/**
 * 获取所有支持的单据类型前缀列表
 * @returns {string[]}
 */
export function getAllBillTypeCodes() {
  return [...VALID_BILL_TYPES]
}

/**
 * 智能转换：将输入转换为标准单据类型
 * 支持输入：前缀(QGD)、中文名称(请购单)、单据编号(QGD001)
 * @param {string} input 输入值
 * @returns {string} 标准单据类型前缀
 */
export function normalizeBillType(input) {
  if (!input) return ''
  
  const trimmed = input.trim().toUpperCase()
  
  // 1. 如果是有效的前缀
  if (VALID_BILL_TYPES.includes(trimmed)) {
    return trimmed
  }
  
  // 2. 如果是单据编号，从中提取
  const fromNo = getBillTypeFromNo(trimmed)
  if (fromNo) return fromNo
  
  // 3. 如果是中文名称
  const fromName = NAME_TO_BILL_TYPE_MAP[input.trim()]
  if (fromName) return fromName
  
  return input // 返回原值
}

/**
 * 格式化为模板名称
 * @param {string} billType 单据类型前缀
 * @returns {string} 模板名称，如 "采购订单模板"
 */
export function formatTemplateName(billType) {
  const name = getBillTypeName(billType)
  return name ? `${name}模板` : billType
}

// 导出常量
export {
  BILL_TYPE_PREFIXES,
  VALID_BILL_TYPES,
  BILL_TYPE_NAME_MAP,
  NAME_TO_BILL_TYPE_MAP,
}

export default {
  getBillTypeFromNo,
  getBillTypeName,
  getBillTypeByName,
  isValidBillType,
  getAllBillTypes,
  getAllBillTypeCodes,
  normalizeBillType,
  formatTemplateName,
}
